import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../api/baseQuery';

// Utility functions for contact data transformation following best practices
const transformContactsToApiFormat = (contacts) => {
    if (!contacts || !Array.isArray(contacts)) return { contactPersons: [], emails: [], phones: [] };
    
    return {
        contactPersons: contacts.map(contact => contact.name).filter(name => name),
        emails: contacts.map(contact => contact.email).filter(email => email),
        phones: contacts.map(contact => contact.phone).filter(phone => phone)
    };
};

const transformApiToContactsFormat = (brand) => {
    const contacts = [];
    const contactPersons = brand.contactPersons || [];
    const emails = brand.emails || [];
    const phones = brand.phones || brand.phone || []; // Support both 'phones' and legacy 'phone'
    
    // Create contact objects from the arrays
    const maxLength = Math.max(contactPersons.length, emails.length, phones.length);
    for (let i = 0; i < maxLength; i++) {
        contacts.push({
            name: contactPersons[i] || '',
            email: emails[i] || '',
            phone: phones[i] || '',
            isPrimary: i === 0
        });
    }
    
    // If no contacts found from arrays, check legacy fields
    if (contacts.length === 0 && (brand.contactPerson || brand.email || brand.phone)) {
        contacts.push({
            name: brand.contactPerson || '',
            email: brand.email || '',
            phone: brand.phone || '',
            isPrimary: true
        });
    }
    
    return contacts;
};

export const brandApi = createApi({
    reducerPath: 'brandApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Brand', 'Category'],
    endpoints: (builder) => ({
        // Get all brands
        getBrands: builder.query({
            query: (params = {}) => {
                const { page = 0, pageSize = 8, keyword = '', status = '', categoryId = '' } = params;
                let url = `/brand/list?page=${page}&pageSize=${pageSize}`;
                
                if (keyword.trim()) {
                    url += `&keyword=${encodeURIComponent(keyword.trim())}`;
                }
                if (status.trim()) {
                    url += `&status=${encodeURIComponent(status)}`;
                }
                if (categoryId.trim()) {
                    url += `&categoryId=${encodeURIComponent(categoryId)}`;
                }
                // Include sort parameters when provided (component handles defaults)
                if (params.sortBy) {
                    url += `&sortBy=${encodeURIComponent(params.sortBy)}`;
                }
                if (params.sortOrder) {
                    url += `&sortOrder=${encodeURIComponent(params.sortOrder)}`;
                }
                
                return {
                    url,
                    method: 'GET'
                };
            },
            transformResponse: (response) => {
                if (!response?.value) {
                    return {
                        brands: [],
                        totalCount: 0,
                        isSuccess: false
                    };
                }
                
                // Transform each brand's contact data using utility function
                const transformedBrands = (response.value.brands || []).map(brand => ({
                    ...brand,
                    contacts: transformApiToContactsFormat(brand),
                    // Transform categoryId to categoryIds array for UI compatibility
                    categoryIds: brand.categoryId ? [brand.categoryId] : (brand.categoryIds || [])
                }));
                
                return {
                    brands: transformedBrands,
                    totalCount: response.value.totalCount || 0,
                    isSuccess: response.isSuccess
                };
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'An error occurred',
                errors: response.data?.errors || [],
                validationErrors: response.data?.validationErrors || []
            }),
            providesTags: ['Brand']
        }),

        // Get brand by ID
        getBrandById: builder.query({
            query: (id) => ({
                url: `/brand/${id}`,
                method: 'GET'
            }),
            transformResponse: (response) => {
                if (!response?.value) {
                    return null;
                }
                
                const brand = response.value;
                
                // Transform API arrays to contacts array format for UI using utility function
                const contacts = transformApiToContactsFormat(brand);
                
                return {
                    ...brand,
                    contacts: contacts,
                    // Transform categoryId to categoryIds array for UI compatibility
                    categoryIds: brand.categoryId ? [brand.categoryId] : (brand.categoryIds || []),
                    isSuccess: response.isSuccess,
                    errors: response.errors || [],
                    validationErrors: response.validationErrors || []
                };
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Brand not found',
                errors: response.data?.errors || [],
                validationErrors: response.data?.validationErrors || []
            }),
            providesTags: (result, error, id) => [{ type: 'Brand', id }]
        }),

        // Get categories
        getCategories: builder.query({
            query: () => ({
                url: '/category/list',
                method: 'GET'
            }),
            transformResponse: (response) => {
                if (!response) {
                    return {
                        value: [],
                        isSuccess: false,
                        errors: [],
                        validationErrors: []
                    };
                }
                
                return {
                    value: response.value || [],
                    isSuccess: response.isSuccess,
                    errors: response.errors || [],
                    validationErrors: response.validationErrors || []
                };
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to load categories',
                errors: response.data?.errors || []
            }),
            providesTags: ['Category']
        }),

        // Create brand
        createBrand: builder.mutation({
            query: (data) => {
                // Transform contacts array to API format using utility function
                const processedContacts = data.contacts && data.contacts.length > 0 
                    ? data.contacts 
                    : data.contactPerson || data.email || data.phone 
                        ? [{
                            name: data.contactPerson || '',
                            email: data.email || '',
                            phone: data.phone || '',
                            isPrimary: true
                          }] 
                        : [];

                const { contactPersons, emails, phones } = transformContactsToApiFormat(processedContacts);

                return {
                    url: '/brand/create',
                    method: 'POST',
                    body: {
                        name: data.name,
                        description: data.description || '',
                        logo: data.logo,
                        location: data.location || '',
                        tenantIds: data.tenantIds || [],
                        // Use single categoryId per new contract
                        categoryId: data.categoryId || (data.categoryIds && data.categoryIds.length > 0 ? data.categoryIds[0] : ''),
                        // Use API expected format
                        contactPersons: contactPersons,
                        emails: emails,
                        phones: phones
                    }
                };
            },
            transformResponse: (response) => {
                console.log('🔧 BrandAPI CreateBrand - Raw Response:', response);
                console.log('📊 Response type:', typeof response);
                console.log('🔍 Response isSuccess:', response?.isSuccess);
                console.log('📋 Response status:', response?.status);
                console.log('⚠️ Response errors:', response?.errors);
                console.log('📝 Response validationErrors:', response?.validationErrors);
                
                const transformedResponse = {
                    ...response,
                    isSuccess: response?.isSuccess || response?.status === 0,
                    errors: response?.errors || [],
                    validationErrors: response?.validationErrors || []
                };
                
                console.log('✅ BrandAPI CreateBrand - Transformed Response:', transformedResponse);
                return transformedResponse;
            },
            transformErrorResponse: (response) => {
                console.error('💥 BrandAPI CreateBrand - Error Response:', response);
                console.error('📊 Error status:', response.status);
                console.error('📋 Error data:', response.data);
                
                const transformedError = {
                    status: response.status,
                    message: response.data?.message || 'Failed to create brand',
                    errors: response.data?.errors || ['An unexpected error occurred'],
                    validationErrors: response.data?.validationErrors || []
                };
                
                console.error('🔧 BrandAPI CreateBrand - Transformed Error:', transformedError);
                return transformedError;
            },
            invalidatesTags: ['Brand']
        }),

        // Update brand
        updateBrand: builder.mutation({
            query: (data) => {
                // Transform contacts array to API format using utility function
                const processedContacts = data.contacts && data.contacts.length > 0 
                    ? data.contacts 
                    : data.contactPerson || data.email || data.phone 
                        ? [{
                            name: data.contactPerson || '',
                            email: data.email || '',
                            phone: data.phone || '',
                            isPrimary: true
                          }] 
                        : [];

                const { contactPersons, emails, phones } = transformContactsToApiFormat(processedContacts);

                return {
                    url: '/brand/update',
                    method: 'PUT',
                    body: {
                        id: data.id,
                        name: data.name,
                        description: data.description || '',
                        logo: data.logo,
                        location: data.location || '',
                        tenantIds: data.tenantIds || [],
                        // Use single categoryId per new contract
                        categoryId: data.categoryId || (data.categoryIds && data.categoryIds.length > 0 ? data.categoryIds[0] : ''),
                        // Use API expected format
                        contactPersons: contactPersons,
                        emails: emails,
                        phones: phones
                    }
                };
            },
            transformResponse: (response) => {
                console.log('🔧 BrandAPI UpdateBrand - Raw Response:', response);
                console.log('📊 Response type:', typeof response);
                console.log('🔍 Response isSuccess:', response?.isSuccess);
                console.log('📋 Response status:', response?.status);
                console.log('⚠️ Response errors:', response?.errors);
                console.log('📝 Response validationErrors:', response?.validationErrors);
                
                const transformedResponse = {
                    ...response,
                    isSuccess: true,
                    errors: response?.errors || [],
                    validationErrors: response?.validationErrors || []
                };
                
                console.log('✅ BrandAPI UpdateBrand - Transformed Response:', transformedResponse);
                return transformedResponse;
            },
            transformErrorResponse: (response) => {
                console.error('💥 BrandAPI UpdateBrand - Error Response:', response);
                console.error('📊 Error status:', response.status);
                console.error('📋 Error data:', response.data);
                
                const transformedError = {
                    status: response.status,
                    message: response.data?.message || 'Failed to update brand',
                    errors: response.data?.errors || ['An unexpected error occurred'],
                    validationErrors: response.data?.validationErrors || []
                };
                
                console.error('🔧 BrandAPI UpdateBrand - Transformed Error:', transformedError);
                return transformedError;
            },
            invalidatesTags: ['Brand']
        }),

        // Delete brand
        deleteBrand: builder.mutation({
            query: (id) => ({
                url: `/brand/${id}`,
                method: 'DELETE'
            }),
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to delete brand',
                errors: response.data?.errors || []
            }),
            invalidatesTags: ['Brand']
        }),

        // Upload brand media
        uploadBrandMedia: builder.mutation({
            query: ({ orderNo, file }) => {
                const formData = new FormData();
                formData.append('orderNo', orderNo);
                formData.append('file', file);
                return {
                    url: '/brand/media',
                    method: 'POST',
                    body: formData,
                    formData: true
                };
            },
            transformResponse: (response) => ({
                ...response,
                isSuccess: response?.isSuccess || response?.status === 0,
                errors: response?.errors || [],
                validationErrors: response?.validationErrors || []
            }),
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to upload image',
                errors: response.data?.errors || ['Upload failed']
            })
        })
    })
});

export const {
    useGetBrandsQuery,
    useGetBrandByIdQuery,
    useCreateBrandMutation,
    useUpdateBrandMutation,
    useDeleteBrandMutation,
    useUploadBrandMediaMutation,
    useGetCategoriesQuery
} = brandApi; 